import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { RoomAssetLink } from '../packageOptions/types';

import { getEgsBeddingLabel, getEgsRecordName, getEgsRoomLabel } from './egsModel';
import type { EgsDraft } from './types';

interface Props {
  drafts: Array<EgsDraft>;
  onEdit: (draft: EgsDraft) => void;
  onRemove: (id: string) => void;
  rooms: Array<RoomAssetLink>;
}

export default function EgsTable({ drafts, onEdit, onRemove, rooms }: Props) {
  if (!drafts.length) {
    return (
      <Typography color="text.secondary" fontStyle="italic" variant="body2">
        No extra guest surcharges added yet.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Room Name</TableCell>
            <TableCell>Extra Guest Type</TableCell>
            <TableCell>Age Range</TableCell>
            <TableCell>Collected By</TableCell>
            <TableCell>Rate Type</TableCell>
            <TableCell>Bedding Type</TableCell>
            <TableCell>Inclusions</TableCell>
            <TableCell align="right">Cost Price</TableCell>
            <TableCell align="right">Converted Cost</TableCell>
            <TableCell align="right">Sell Price (AUD)</TableCell>
            <TableCell align="right">% Margin</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.map((draft) => (
            <TableRow key={draft.id}>
              <TableCell>
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  {draft.sourceName && (
                    <Tooltip title={`Copied from the Salesforce EGS "${draft.sourceName}"`}>
                      <InfoOutlinedIcon color="action" fontSize="inherit" />
                    </Tooltip>
                  )}
                  <Tooltip title={draft.rooms === 'all' ? '' : getEgsRoomLabel(draft, rooms)}>
                    <span>{getEgsRecordName(draft, rooms)}</span>
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell>{draft.guestType}</TableCell>
              <TableCell>{`${draft.fromAge} - ${draft.toAge}`}</TableCell>
              <TableCell>{draft.collectedBy}</TableCell>
              <TableCell>{draft.rateType || '—'}</TableCell>
              <TableCell>{getEgsBeddingLabel(draft)}</TableCell>
              <TableCell>{draft.inclusions}</TableCell>
              <TableCell align="right">{draft.costPrice.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.convertedCost.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.sellPriceAud.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.marginPct != null ? `${draft.marginPct.toFixed(2)}%` : '—'}</TableCell>
              <TableCell>
                <IconButton aria-label="Edit" onClick={() => onEdit(draft)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton aria-label="Delete" onClick={() => onRemove(draft.id)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
